import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { filesApi } from '../../services/fileShareApi';
import { format } from 'date-fns';

/**
 * Successful Translate / Re-translate jobs for one file (version, time, size, status).
 * URN stays in DB for viewing; not shown in this list.
 */
const TranslationHistoryDialog = ({ open, onClose, file }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && file) {
      loadHistory();
    } else if (!open) {
      setVersions([]);
      setError(null);
    }
  }, [open, file]);

  const loadHistory = async () => {
    const fileId = file?.fileId || file?.id;
    if (!fileId) {
      setError('File id missing');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await filesApi.getVersions(fileId);
      const rows = Array.isArray(response.data) ? response.data : [];
      const successful = rows
        .filter((v) => {
          const forge = (v.forgeStatus || '').toUpperCase();
          const translation = (v.translationStatus || '').toUpperCase();
          return forge === 'SUCCESS' || forge === 'COMPLETE' || translation === 'SUCCESS';
        })
        .sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0));
      setVersions(successful);
    } catch (err) {
      console.error('Error loading translation history:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return String(dateString);
    }
  };

  const formatSize = (bytes) => {
    if (bytes == null || Number.isNaN(Number(bytes))) return '—';
    const n = Number(bytes);
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  const fileLabel = file?.fileName || file?.name || 'file';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <HistoryIcon fontSize="small" />
        Translation history
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Successful Translate / Re-translate jobs for <strong>{fileLabel}</strong>
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : versions.length === 0 ? (
          <Alert severity="info">No successful translations recorded yet.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell>
                  <TableCell>Translated at</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.map((v) => (
                  <TableRow key={v.versionId || v.versionNumber}>
                    <TableCell>v{v.versionNumber}</TableCell>
                    <TableCell>{formatDate(v.createdAt)}</TableCell>
                    <TableCell>{formatSize(v.fileSize)}</TableCell>
                    <TableCell>
                      <Chip label="SUCCESS" color="success" size="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TranslationHistoryDialog;
